import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { pickContrastingForeground } from '../utils/contrast.util';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'dmp-theme';
const TRANSITION_MS = 300;

/** Surface tokens whose foreground color is derived from the background for readable tabs. */
const CONTRAST_PAIRS: ReadonlyArray<{ background: string; foreground: string }> = [
	{ background: '--accent-surface', foreground: '--accent-surface-fg' },
	{ background: '--primary', foreground: '--primary-foreground' },
];

@Injectable({ providedIn: 'root' })
export class ThemeService {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly _theme = new BehaviorSubject<ThemeMode>(this.loadTheme());

	readonly theme$ = this._theme.asObservable();

	constructor() {
		this.applyTheme(this._theme.value, false);
	}

	get theme(): ThemeMode {
		return this._theme.value;
	}

	get isDark(): boolean {
		return this._theme.value === 'dark';
	}

	setTheme(mode: ThemeMode): void {
		if (mode === this._theme.value) {
			return;
		}
		this._theme.next(mode);
		this.persistTheme(mode);
		this.applyTheme(mode, true);
	}

	toggleTheme(): void {
		this.setTheme(this._theme.value === 'light' ? 'dark' : 'light');
	}

	private loadTheme(): ThemeMode {
		// App always boots in light theme; users can still toggle during the session.
		if (isPlatformBrowser(this.platformId)) {
			localStorage.setItem(STORAGE_KEY, 'light');
		}
		return 'light';
	}

	private persistTheme(mode: ThemeMode): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		localStorage.setItem(STORAGE_KEY, mode);
	}

	private applyTheme(mode: ThemeMode, animate: boolean): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		const root = document.documentElement;
		root.setAttribute('data-theme', mode);
		root.style.colorScheme = mode;
		this.syncContrastTokens(root);

		if (!animate) {
			return;
		}

		root.classList.add('theme-transition');
		window.setTimeout(() => root.classList.remove('theme-transition'), TRANSITION_MS);
	}

	/** Recompute text-on-color tokens after something else (tenant branding) changes the surface colors. */
	refreshContrastTokens(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}
		this.syncContrastTokens(document.documentElement);
	}

	/** Keep active-tab foreground colors readable when surface backgrounds change per theme. */
	private syncContrastTokens(root: HTMLElement): void {
		const styles = getComputedStyle(root);

		for (const { background, foreground } of CONTRAST_PAIRS) {
			const backgroundColor = styles.getPropertyValue(background).trim();
			if (!backgroundColor) {
				continue;
			}

			root.style.setProperty(foreground, pickContrastingForeground(backgroundColor));
		}
	}
}
